import { ParentApi } from "@websim/core/src/parent-api/types";
import { Message } from "@websim/core/src/sites/message";
import { Stub } from "@websim/message-proxy/client";
import {
  bridgeError,
  normalizeMessageImagesInPage,
  requestAgentChatCompletion,
} from "./ai-runtime";

/** An OpenAI-shaped tool definition, or the shorthand map form. */
export type ChatToolDefinition =
  | {
      type: "function";
      function: { name: string; description?: string; parameters?: unknown };
    }
  | { name: string; description?: string; parameters?: unknown };

export interface ChatToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

export interface ChatCompletionUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  websim?: {
    cost_credits: number;
    charged: boolean;
    free_remaining_credits: number;
    free_project_eligible?: boolean;
    cached: boolean;
  };
}

export interface ChatCompletionMessage {
  role: "assistant";
  content: string;
  tool_calls?: ChatToolCall[];
  usage?: ChatCompletionUsage;
}

/** One item of the async iterable a `stream: true` call returns. */
export interface ChatCompletionChunk {
  /** Text added by this chunk (empty on the final item). */
  delta: string;
  /** Everything streamed so far. */
  content: string;
  /** True on the last item, which also carries the final message fields. */
  done: boolean;
  tool_calls?: ChatToolCall[];
  usage?: ChatCompletionUsage;
}

export interface ChatCompletionOptions {
  messages: Array<Message | Record<string, unknown>>;
  /** Best-effort JSON mode. Prefer `schema`. */
  json?: boolean;
  /** A JSON schema the answer must match (strict structured output). */
  schema?: Record<string, unknown>;
  /** Tools the model may call; the caller executes them and calls again. */
  tools?: ChatToolDefinition[] | Record<string, Omit<ChatToolDefinition, "name" | "type">>;
  tool_choice?: unknown;
  /** Return an async iterable of chunks instead of one message. */
  stream?: boolean;
  max_tokens?: number;
  temperature?: number;
  /** Set false to bypass the same-input answer cache. */
  cache?: boolean;
  /** Abort the host request and upstream model stream. */
  signal?: AbortSignal;
}

export interface IChatCompletion {
  completions: {
    create: {
      (options: ChatCompletionOptions & { stream: true }): Promise<
        AsyncIterable<ChatCompletionChunk>
      >;
      (options: ChatCompletionOptions): Promise<ChatCompletionMessage>;
    };
  };
}

/** Accept both the OpenAI array form and `{ name: { description, parameters } }`. */
export function normalizeChatTools(
  tools: ChatCompletionOptions["tools"]
): Array<{ type: "function"; function: Record<string, unknown> }> | undefined {
  if (!tools) return undefined;
  const entries = Array.isArray(tools)
    ? tools.map((tool) =>
        "function" in tool
          ? tool
          : { type: "function" as const, function: { ...tool } }
      )
    : Object.entries(tools).map(([name, spec]) => ({
        type: "function" as const,
        function: { name, ...(spec as Record<string, unknown>) },
      }));
  return entries.map((tool) => {
    const fn = { ...(tool.function as Record<string, unknown>) };
    // The AI SDK's name for the schema, accepted as an alias.
    if (fn.inputSchema !== undefined && fn.parameters === undefined) {
      fn.parameters = fn.inputSchema;
      delete fn.inputSchema;
    }
    delete fn.execute;
    return { type: "function" as const, function: fn };
  });
}

/** Parse `data:` lines out of an SSE byte stream, yielding each JSON payload. */
export async function* sseJson(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<Record<string, unknown>> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completed = false;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newline = buffer.indexOf("\n");
      while (newline !== -1) {
        const line = buffer.slice(0, newline).trim();
        buffer = buffer.slice(newline + 1);
        newline = buffer.indexOf("\n");
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "" || payload === "[DONE]") continue;
        try {
          yield JSON.parse(payload);
        } catch {
          // a partial or non-JSON line: skip
        }
      }
    }
    completed = true;
  } finally {
    if (!completed) await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

function mergeToolCalls(
  into: ChatToolCall[],
  deltas: Array<Record<string, unknown>>
): void {
  for (const raw of deltas) {
    const index = typeof raw.index === "number" ? raw.index : into.length;
    let call = into[index];
    if (!call) {
      call = { id: "", type: "function", function: { name: "", arguments: "" } };
      into[index] = call;
    }
    if (typeof raw.id === "string") call.id = raw.id;
    const fn = raw.function as { name?: unknown; arguments?: unknown } | undefined;
    if (typeof fn?.name === "string") call.function.name += fn.name;
    if (typeof fn?.arguments === "string") call.function.arguments += fn.arguments;
  }
}

export class Chat implements IChatCompletion {
  constructor(private parentApi: Stub<ParentApi>) {}

  completions = {
    create: (async (options: ChatCompletionOptions) => {
      const {
        messages,
        json,
        schema,
        tools,
        tool_choice,
        stream,
        max_tokens,
        temperature,
        cache,
        signal,
      } = options;
      // A plain single-turn call stays on chatCompletion (run_chat_completion
      // and its own 1h cache). Tools, a schema, streaming, sampling params,
      // cache opt-out, or cancellation go through the agent-step endpoint.
      const advanced =
        schema !== undefined ||
        tools !== undefined ||
        stream === true ||
        max_tokens !== undefined ||
        temperature !== undefined ||
        cache === false ||
        signal !== undefined;
      if (!advanced) {
        return this.parentApi.chatCompletion({ messages: messages as Message[], json });
      }

      // Preserve Blobs/canvases long enough to normalize them; JSON cloning
      // first turns those values into `{}` before the image code can see them.
      const normalizedMessages = messages.map((message) => {
        const copy = { ...(message as Record<string, unknown>) };
        if (Array.isArray(copy.content)) {
          copy.content = copy.content.map((part) =>
            part && typeof part === "object"
              ? { ...(part as Record<string, unknown>) }
              : part
          );
        }
        return copy;
      });
      await normalizeMessageImagesInPage(normalizedMessages);

      const request: Record<string, unknown> = {
        messages: JSON.parse(JSON.stringify(normalizedMessages)),
        ...(tools ? { tools: normalizeChatTools(tools) } : {}),
        ...(tool_choice !== undefined ? { tool_choice } : {}),
        ...(schema
          ? {
              response_format: {
                type: "json_schema",
                json_schema: { name: "response", schema, strict: true },
              },
            }
          : json
            ? { response_format: { type: "json_object" } }
            : {}),
        ...(max_tokens !== undefined ? { max_tokens } : {}),
        ...(temperature !== undefined ? { temperature } : {}),
        ...(cache === false ? { cache: false } : {}),
        stream: stream === true,
      };
      const response = await requestAgentChatCompletion(
        this.parentApi,
        request,
        signal
      );
      if (!response.ok) throw await bridgeError(response);

      if (!stream) {
        const body = (await response.json()) as {
          choices?: Array<{ message?: { content?: string | null; tool_calls?: ChatToolCall[] } }>;
          usage?: ChatCompletionUsage;
        };
        const message = body.choices?.[0]?.message ?? {};
        const out: ChatCompletionMessage = {
          role: "assistant",
          content: message.content ?? "",
          ...(message.tool_calls?.length ? { tool_calls: message.tool_calls } : {}),
          ...(body.usage ? { usage: body.usage } : {}),
        };
        return out;
      }

      const bodyStream = response.body;
      if (!bodyStream) throw new Error("Empty stream");
      const iterate = async function* (): AsyncGenerator<ChatCompletionChunk> {
        let content = "";
        const toolCalls: ChatToolCall[] = [];
        let usage: ChatCompletionUsage | undefined;
        for await (const chunk of sseJson(bodyStream)) {
          if (chunk.usage && typeof chunk.usage === "object") {
            usage = chunk.usage as ChatCompletionUsage;
          }
          const choice = (chunk.choices as Array<Record<string, unknown>> | undefined)?.[0];
          const delta = choice?.delta as Record<string, unknown> | undefined;
          if (!delta) continue;
          if (Array.isArray(delta.tool_calls)) {
            mergeToolCalls(toolCalls, delta.tool_calls as Array<Record<string, unknown>>);
          }
          if (typeof delta.content === "string" && delta.content.length > 0) {
            content += delta.content;
            yield { delta: delta.content, content, done: false };
          }
        }
        yield {
          delta: "",
          content,
          done: true,
          ...(toolCalls.length ? { tool_calls: toolCalls.filter(Boolean) } : {}),
          ...(usage ? { usage } : {}),
        };
      };
      return iterate();
    }) as IChatCompletion["completions"]["create"],
  };
}
